"use client";

import Image from "next/image";

import { Reveal } from "@/components/motion/Reveal";
import { Container } from "@/components/primitives/Container";
import { getSiteContent } from "@/content/site";
import { useLocale } from "@/lib/i18n/provider";

const linkClassName =
  "flex h-10 items-center rounded px-6 opacity-55 transition-opacity duration-300 hover:opacity-85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-base)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg-base)]";

const logoClassName =
  "h-8 w-auto object-contain [filter:var(--logo-filter-negative)]";

/** Official ENISA emerging-company seal. Proper name; the badge copy is Spanish. */
function enisaLabel(locale: string): string {
  if (locale === "es" || locale === "ca") {
    return "ENISA, certificada como empresa emergente. Ministerio de Industria y Turismo";
  }
  return "ENISA certified emerging company (Certificada como empresa emergente)";
}

export function InCollaboration() {
  const { locale } = useLocale();
  const { collaboration } = getSiteContent(locale);
  const enisaAlt = enisaLabel(locale);

  return (
    <section
      aria-label={collaboration.sectionLabel}
      className="border-t border-[var(--color-surface-border)] py-14 md:py-16"
    >
      <Container>
        <div className="flex flex-col items-center gap-6 text-center">
          <Reveal amount={0.3}>
            <span className="text-eyebrow text-[var(--color-text-muted)]">
              {collaboration.sectionLabel}
            </span>
          </Reveal>

          <Reveal delay={0.1} amount={0.25}>
            <ul
              role="list"
              className="flex flex-wrap items-center justify-center gap-y-4"
            >
              <li>
                <a
                  href={collaboration.primaryPartner.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={collaboration.primaryPartner.label}
                  className={linkClassName}
                >
                  <Image
                    src="/assets/brand/partners/fpcm-white.png"
                    alt={collaboration.primaryPartner.imageAlt}
                    width={120}
                    height={32}
                    className={logoClassName}
                  />
                </a>
              </li>

              <li aria-hidden="true" className="h-7 w-px bg-[var(--color-surface-border)]" />

              {collaboration.secondaryPartners.map((partner) => (
                <li key={partner.label}>
                  <a
                    href={partner.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={partner.label}
                    className={linkClassName}
                  >
                    <Image
                      src="/assets/brand/partners/madrid-emprende.svg"
                      alt={partner.imageAlt}
                      width={130}
                      height={32}
                      className={logoClassName}
                    />
                  </a>
                </li>
              ))}

              <li aria-hidden="true" className="h-7 w-px bg-[var(--color-surface-border)]" />

              <li>
                <a
                  href="https://www.enisa.es/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={enisaAlt}
                  className="flex items-center rounded px-6 opacity-55 transition-opacity duration-300 hover:opacity-85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-base)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg-base)]"
                >
                  <Image
                    src="/assets/brand/partners/enisa-certificada.png"
                    alt={enisaAlt}
                    width={2961}
                    height={2164}
                    className="enisa-seal h-16 w-auto object-contain sm:h-20"
                  />
                </a>
              </li>
            </ul>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}

export default InCollaboration;
